import { SafeError, SafePromise } from '@benzinga/safe-await';
import { ListenableSubscribable } from '@benzinga/subscribable';
import { NewsBZQLQuery, NewsfeedFieldType, NodeId, RestfulStory } from './entities';
import { AdvancedNewsContentRestful, AdvancedNewsFiltersRestful, NewsInternalContentRestful } from './restful';
import { ContentTypes, ingressContextTypes } from './ingress';
import { Session } from '@benzinga/session';
import { expressionConvert } from './internal-utils';

interface NewsRequestErrorEvent {
  error: SafeError;
  errorType: 'news:get_content_types_error' | 'news:get_story_error' | 'news:get_stories_error';
  type: 'error';
}

export type NewsRequestEvent = NewsRequestErrorEvent;

export type NewsRequestSubscription = ReturnType<NewsRequest['subscribe']>;

export class NewsRequest extends ListenableSubscribable<NewsRequestEvent> {
  private session: Session;
  private restfulFilters: AdvancedNewsFiltersRestful;
  private restfulContent: AdvancedNewsContentRestful;
  private restfulInternalContent: NewsInternalContentRestful;

  constructor(session: Session) {
    super();
    this.session = session;
    this.restfulFilters = new AdvancedNewsFiltersRestful(session, { 'x-device-key': true });
    this.restfulContent = new AdvancedNewsContentRestful(session);
    this.restfulInternalContent = new NewsInternalContentRestful(session);
  }

  public getContentTypes = async (): SafePromise<ContentTypes> => {
    const contentTypes = await ingressContextTypes(this.restfulFilters.getContentTypes());
    if (contentTypes.err) {
      this.dispatch({ error: contentTypes.err, errorType: 'news:get_content_types_error', type: 'error' });
      return contentTypes;
    }
    return contentTypes;
  };

  public getStory = async (nodeId: NodeId): SafePromise<RestfulStory> => {
    const story = await this.restfulContent.getStory(nodeId);
    if (story.err) {
      this.dispatch({ error: story.err, errorType: 'news:get_story_error', type: 'error' });
      return { err: story.err };
    }
    return story;
  };

  public getStories = async (query: Partial<NewsBZQLQuery>): SafePromise<RestfulStory[]> => {
    const definedQuery = {
      fields: query.fields ?? [],
      limit: query.limit ?? 100,
      offset: query.offset ?? 0,
      sort: query.sort ?? ({} as Record<NewsfeedFieldType, -1 | 1>),
      where: query.where ? query.where.toBZQL(expressionConvert(this.session) as any) : null,
    };

    const stories = await this.restfulInternalContent.getStories(definedQuery);
    if (stories.err) {
      this.dispatch({ error: stories.err, errorType: 'news:get_stories_error', type: 'error' });
      return { err: stories.err };
    }
    return { ok: stories.ok.data };
  };
}
